class ExpressOrderSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.searchResultsContainer = this.querySelector('#search-results');
    this.input = this.querySelector('#sku-search-input');
    this.allExpressOrderSearchInstances = document.querySelectorAll('express-order-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.cartQuantities = {};

    this.setupEventListeners();
  }

  refreshCartQuantities() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        const map = {};
        if (cart && Array.isArray(cart.items)) {
          for (const item of cart.items) {
            const id = String(item.variant_id);
            map[id] = (map[id] || 0) + (parseInt(item.quantity, 10) || 0);
          }
        }
        this.cartQuantities = map;
      })
      .catch(() => {
        this.cartQuantities = {};
      });
  }

  getQuickOrderQty(variantId) {
    const cart = document.querySelector('express-order-cart');
    if (!cart || typeof cart.getProducts !== 'function') return 0;
    const id = String(variantId);
    return cart
      .getProducts()
      .filter((p) => String(p.variantId) === id)
      .reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 0), 0);
  }

  getCumulativeQty(variantId) {
    const cartQty = this.cartQuantities[String(variantId)] || 0;
    return cartQty + this.getQuickOrderQty(variantId);
  }

  setupEventListeners() {
    if (!this.input) {
      return;
    }

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));

    if (this.searchResultsContainer) {
      this.searchResultsContainer.addEventListener('mousedown', (event) => {
        if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        event.preventDefault();
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInput(event) {
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.clearResults();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, 300);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onInput();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeydown(event) {
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      case 'Escape':
        this.close(true);
        break;
    }
  }

  clearResults() {
    if (this.searchResultsContainer) {
      this.searchResultsContainer.innerHTML = '';
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.express-order-search__item')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      selectedOption.click();
      return;
    }

    const addButtons = this.querySelectorAll('.express-order__add-btn[data-action="add"]');
    if (addButtons.length === 1) {
      addButtons[0].click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.refreshCartQuantities().then(() => this.renderSearchResults(this.cachedResults[queryKey]));
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const searchUrl = `/search?q=${encodeURIComponent(searchTerm)}&section_id=express-order-search`;

    Promise.all([
      fetch(searchUrl, { signal: this.abortController.signal }),
      this.refreshCartQuantities(),
    ])
      .then(([response]) => response)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        let section = doc.querySelector('#shopify-section-express-order-search');
        if (!section) {
          section = doc.querySelector('[id*="express-order-search"]');
        }
        if (!section) {
          section = doc.querySelector('section');
        }

        if (section) {
          const resultsMarkup = section.innerHTML;

          this.allExpressOrderSearchInstances.forEach((searchInstance) => {
            searchInstance.cachedResults[queryKey] = resultsMarkup;
          });

          this.renderSearchResults(resultsMarkup);
        } else {
          this.renderSearchResults('<p class="express-order__no-results">Aucun produit trouvé</p>');
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
      });
  }

  renderSearchResults(resultsMarkup) {
    if (this.searchResultsContainer) {
      if (!resultsMarkup || resultsMarkup.trim().length < 10) {
        console.warn('No results markup to render');
      } else {
        this.searchResultsContainer.innerHTML = resultsMarkup;
      }

      this.setAttribute('results', true);
      this.open();

      this.setupResultsEventListeners();
    }
  }

  setupResultsEventListeners() {
    const addButtons = this.searchResultsContainer.querySelectorAll('.express-order__add-btn[data-action="add"]');
    addButtons.forEach((button) => {
      button.addEventListener('click', this.handleAddProduct.bind(this));
    });

    const qtyButtons = this.searchResultsContainer.querySelectorAll('.express-order__qty-btn');
    qtyButtons.forEach((button) => {
      button.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    const qtyInputs = this.searchResultsContainer.querySelectorAll('.express-order__qty-input');
    qtyInputs.forEach((input) => {
      input.addEventListener('input', this.handleQuantityInput.bind(this));
      input.addEventListener('change', this.handleQuantityInput.bind(this));
    });

    this.searchResultsContainer.querySelectorAll('.express-order__search-result-item').forEach((item) => {
      this.updateUnitPriceDisplay(item);
    });
  }

  parsePriceBreaks(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((b) => ({
          minimum_quantity: parseInt(b.minimum_quantity, 10),
          price: parseInt(b.price, 10),
        }))
        .filter((b) => Number.isFinite(b.minimum_quantity) && Number.isFinite(b.price))
        .sort((a, b) => b.minimum_quantity - a.minimum_quantity);
    } catch (e) {
      return [];
    }
  }

  getEffectiveUnitPrice(basePrice, breaks, quantity) {
    if (!breaks || breaks.length === 0) return basePrice;
    for (const pb of breaks) {
      if (quantity >= pb.minimum_quantity) return pb.price;
    }
    return basePrice;
  }

  formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }

  updateUnitPriceDisplay(resultItem) {
    const addBtn = resultItem.querySelector('.express-order__add-btn[data-action="add"]');
    const priceEl = resultItem.querySelector('.express-order__price');
    const qtyInput = resultItem.querySelector('.express-order__qty-input');
    if (!addBtn || !priceEl || !qtyInput) return;

    const basePrice = parseInt(priceEl.dataset.basePrice ?? addBtn.dataset.price, 10) || 0;
    const breaks = this.parsePriceBreaks(addBtn.dataset.priceBreaks);
    const inputQty = parseInt(qtyInput.value, 10) || 1;
    const cumulativeQty = this.getCumulativeQty(addBtn.dataset.variantId) + inputQty;
    const effective = this.getEffectiveUnitPrice(basePrice, breaks, cumulativeQty);

    priceEl.textContent = this.formatMoney(effective);
    priceEl.classList.toggle('express-order__price--discounted', effective < basePrice);
  }

  handleQuantityInput(event) {
    const resultItem = event.currentTarget.closest('.express-order__search-result-item');
    if (resultItem) this.updateUnitPriceDisplay(resultItem);
  }

  handleAddProduct(event) {
    const button = event.target;
    const qtyInput = button.closest('.express-order__search-result-actions').querySelector('.express-order__qty-input');
    const quantity = parseInt(qtyInput.value) || 1;

    const productData = {
      variantId: button.dataset.variantId,
      handle: button.dataset.handle || '',
      title: button.dataset.productTitle || '',
      variantTitle: button.dataset.variantTitle || '',
      price: parseInt(button.dataset.price) || 0,
      compareAtPrice: parseInt(button.dataset.compareAtPrice) || 0,
      quantity: quantity,
      image: button.dataset.image || '',
      sku: button.dataset.sku || '',
      salesMultiple: parseInt(button.dataset.salesMultiple) || 0,
      priceBreaks: this.parsePriceBreaks(button.dataset.priceBreaks),
    };

    document.dispatchEvent(
      new CustomEvent('express-order-add-product', {
        detail: productData,
      }),
    );

    this.close(true);
    if (this.input) this.input.focus();
  }

  handleQuantityChange(event) {
    const button = event.currentTarget;
    const action = button.dataset.action;
    const resultItem = button.closest('.express-order__search-result-item');
    const qtyInput = button.closest('.express-order__quantity-controls').querySelector('.express-order__qty-input');
    const salesMultiple = parseInt(resultItem?.dataset.salesMultiple) || 0;
    const stepValue = salesMultiple > 0 ? salesMultiple : 1;
    const minValue = salesMultiple > 0 ? salesMultiple : 1;
    let currentValue = parseInt(qtyInput.value) || minValue;

    if (action === 'increase') {
      currentValue = currentValue + stepValue;
    } else if (action === 'decrease') {
      currentValue = Math.max(currentValue - stepValue, minValue);
    }

    qtyInput.value = currentValue;

    if (resultItem) this.updateUnitPriceDisplay(resultItem);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.clearResults();
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }
}

customElements.define('express-order-search', ExpressOrderSearch);
